import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Wand2, Phone, Check, Loader2, Printer, Mail, RotateCcw, Trophy, Award, ChevronRight } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { COMPANY } from "../lib/data";
import { getVisitorId, trackEvent, getAttribution } from "../lib/track";
import { QUESTIONS, isStepComplete, scoreMats, buildSelectionSummary } from "../lib/matSelector";
import { Seo } from "../components/site/Seo";

export default function MatSelector() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const avenue = searchParams.get("avenue") === "rent" ? "rent" : "buy";
  const [mats, setMats] = useState([]);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [emailForm, setEmailForm] = useState({ name: "", email: "", company: "" });
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/mats").then(({ data }) => setMats(data)).catch(() => {});
    trackEvent("calculator_open", { label: "Ground Protection Mat Selector", product: "Ground Protection" });
  }, []);

  const recommendations = useMemo(
    () => (showResults ? scoreMats(mats, answers) : []),
    [showResults, mats, answers]
  );

  const total = QUESTIONS.length;
  const q = QUESTIONS[step];
  const progress = Math.round(((showResults ? total : step) / total) * 100);

  const pick = (key, value) => setAnswers((a) => ({ ...a, [key]: value }));
  const setText = (key, value) => setAnswers((a) => ({ ...a, [key]: value }));

  const next = () => {
    if (step < total - 1) setStep(step + 1);
    else {
      setShowResults(true);
      trackEvent("calculator_complete", { label: "Ground Protection Mat Selector completed", product: "Ground Protection" });
    }
  };
  const back = () => { if (step > 0) setStep(step - 1); };

  const restart = () => { setAnswers({}); setStep(0); setShowResults(false); setEmailOpen(false); setEmailSent(false); };

  const goToQuote = (matName) => {
    const summary = buildSelectionSummary(answers, recommendations);
    const qs = new URLSearchParams({
      avenue, source: "mat-selector", mat: matName, detail: summary,
    });
    navigate(`/request-quote?${qs.toString()}`);
  };

  const emailResults = async (e) => {
    e.preventDefault();
    if (!emailForm.name || !emailForm.email) { toast.error("Name and email are required."); return; }
    setSending(true);
    const summary = buildSelectionSummary(answers, recommendations);
    try {
      await api.post("/quote", {
        name: emailForm.name, email: emailForm.email, company: emailForm.company || "—",
        industry: "Ground Protection Mat Selector", message: summary,
        products: recommendations.map((r) => r.mat.name).slice(0, 1),
        visitor_id: getVisitorId(), attribution: getAttribution(),
      });
      await trackEvent("calculator_complete", { email: emailForm.email, label: "Mat Selector results emailed", product: "Ground Protection" });
      setEmailSent(true);
      toast.success("Results sent. Our team will follow up shortly.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setSending(false); }
  };

  return (
    <div data-testid="mat-selector-page" className="min-h-screen bg-charcoal text-white">
      <Seo title="Mat Selector Tool" description="Answer a few quick questions about your equipment loads, ground conditions, and access needs — and get a matched ground-protection matting recommendation from OneHarvest." />
      {/* Top bar */}
      <div className="sticky top-0 z-40 bg-ink border-b border-white/10 print:hidden">
        <div className="max-w-4xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/ground-protection" className="inline-flex items-center gap-2 text-white/70 hover:text-flame text-sm">
            <ArrowLeft className="h-4 w-4" /> Ground Protection
          </Link>
          <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-white/50">
            <Wand2 className="h-3.5 w-3.5 text-flame" /> Mat Selector
          </span>
        </div>
        {/* Progress */}
        <div className="h-1 bg-white/5">
          <div className="h-full bg-flame transition-all duration-500" style={{ width: `${progress}%` }} data-testid="selector-progress" />
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-10 md:py-14">
        {!showResults ? (
          <div data-testid="wizard-step">
            <div className="text-[11px] uppercase tracking-[0.2em] text-flame font-semibold">
              Step {step + 1} of {total}
            </div>
            <h1 className="mt-3 font-display font-black text-3xl md:text-4xl tracking-tight">{q.title}</h1>
            {q.subtitle && <p className="mt-3 text-white/60 leading-relaxed max-w-2xl">{q.subtitle}</p>}

            <div className="mt-8">
              {(q.kind === "single" || q.kind === "single+text") && (
                <div className="grid sm:grid-cols-2 gap-3" data-testid="option-grid">
                  {q.options.map((o) => (
                    <OptionButton key={o.value} active={answers[q.id] === o.value}
                      testid={`opt-${q.id}-${o.value}`} onClick={() => pick(q.id, o.value)}
                      label={o.label} hint={o.hint} />
                  ))}
                </div>
              )}

              {q.kind === "single+text" && (
                <div className="mt-5 max-w-md">
                  <label className="block text-sm text-white/70 mb-2">{q.textLabel}</label>
                  <input data-testid={`text-${q.textKey}`} value={answers[q.textKey] || ""}
                    onChange={(e) => setText(q.textKey, e.target.value)} placeholder={q.textPlaceholder}
                    className="w-full bg-white/5 border border-white/15 px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-flame" />
                </div>
              )}

              {q.kind === "text" && (
                <div className="max-w-md">
                  <label className="block text-sm text-white/70 mb-2">{q.textLabel}</label>
                  <input data-testid={`text-${q.textKey}`} value={answers[q.textKey] || ""}
                    onChange={(e) => setText(q.textKey, e.target.value)} placeholder={q.textPlaceholder}
                    className="w-full bg-white/5 border border-white/15 px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-flame" />
                </div>
              )}

              {q.kind === "dual" && (
                <div className="space-y-6">
                  {q.groups.map((g) => (
                    <div key={g.key}>
                      <div className="text-sm text-white/70 mb-2.5">{g.label}</div>
                      <div className="grid sm:grid-cols-2 gap-3">
                        {g.options.map((o) => (
                          <OptionButton key={o.value} active={answers[g.key] === o.value}
                            testid={`opt-${g.key}-${o.value}`} onClick={() => pick(g.key, o.value)}
                            label={o.label} hint={o.hint} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Nav */}
            <div className="mt-10 flex items-center justify-between gap-4">
              <button data-testid="selector-back" onClick={back} disabled={step === 0}
                className="inline-flex items-center gap-2 text-white/60 hover:text-white disabled:opacity-30 transition-colors">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
              <button data-testid="selector-next" onClick={next} disabled={!isStepComplete(step, answers)}
                className="inline-flex items-center gap-2 bg-flame text-white px-7 py-3.5 font-semibold hover:bg-flame/90 disabled:opacity-40 transition-colors">
                {step === total - 1 ? "See my recommendation" : "Continue"} <ArrowRight className="h-4 w-4" />
              </button>
            </div>

            {/* Fallback CTA — every step */}
            <SpecialistFallback />
          </div>
        ) : (
          <div data-testid="selector-results">
            <div className="print:block hidden mb-6">
              <div className="font-display font-black text-2xl text-white">OneHarvest — Mat Selector Results</div>
            </div>
            <div className="flex items-center gap-2 text-flame text-[11px] uppercase tracking-[0.2em] font-semibold">
              <Check className="h-4 w-4" /> Your recommendation
            </div>
            <h1 className="mt-3 font-display font-black text-3xl md:text-4xl tracking-tight">
              {recommendations.length} best-fit {recommendations.length === 1 ? "mat" : "mats"} for your job.
            </h1>
            <p className="mt-3 text-white/60 max-w-2xl">
              Ranked from your inputs. Pricing is provided on request — get a quote for any option or email these results to yourself.
            </p>

            {/* Action bar */}
            <div className="mt-6 flex flex-wrap gap-3 print:hidden">
              <button data-testid="print-results" onClick={() => window.print()}
                className="inline-flex items-center gap-2 border border-white/20 px-5 py-2.5 text-sm font-semibold hover:border-flame hover:text-flame transition-colors">
                <Printer className="h-4 w-4" /> Print / Save PDF
              </button>
              <button data-testid="email-results-toggle" onClick={() => setEmailOpen((v) => !v)}
                className="inline-flex items-center gap-2 border border-white/20 px-5 py-2.5 text-sm font-semibold hover:border-flame hover:text-flame transition-colors">
                <Mail className="h-4 w-4" /> Email me these results
              </button>
              <button data-testid="restart-selector" onClick={restart}
                className="inline-flex items-center gap-2 border border-white/20 px-5 py-2.5 text-sm font-semibold hover:border-flame hover:text-flame transition-colors">
                <RotateCcw className="h-4 w-4" /> Start over
              </button>
            </div>

            {/* Email capture */}
            {emailOpen && (
              <div className="mt-5 border border-white/15 bg-white/[0.03] p-6 print:hidden" data-testid="email-results-form">
                {emailSent ? (
                  <div className="flex items-center gap-3 text-emerald-300">
                    <Check className="h-5 w-5" /> Sent! Check your inbox — our team will follow up shortly.
                  </div>
                ) : (
                  <form onSubmit={emailResults} className="grid sm:grid-cols-3 gap-3 items-end">
                    <FieldDark label="Full name *" testid="email-name" value={emailForm.name} onChange={(e) => setEmailForm({ ...emailForm, name: e.target.value })} />
                    <FieldDark label="Email *" testid="email-email" type="email" value={emailForm.email} onChange={(e) => setEmailForm({ ...emailForm, email: e.target.value })} />
                    <FieldDark label="Company" testid="email-company" value={emailForm.company} onChange={(e) => setEmailForm({ ...emailForm, company: e.target.value })} />
                    <button data-testid="email-submit" disabled={sending}
                      className="sm:col-span-3 inline-flex items-center justify-center gap-2 bg-flame text-white px-6 py-3 font-semibold hover:bg-flame/90 disabled:opacity-50 transition-colors">
                      {sending ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending...</> : <>Send me the results</>}
                    </button>
                  </form>
                )}
              </div>
            )}

            {/* Recommendation cards */}
            <div className="mt-8 space-y-5">
              {recommendations.map((r, i) => (
                <div key={r.mat.id} data-testid={`rec-${r.mat.key}`}
                  className={`border bg-white/[0.02] overflow-hidden ${i === 0 ? "border-flame/50" : "border-white/10"}`}>
                  <div className="grid md:grid-cols-12">
                    <div className="md:col-span-4 relative aspect-[4/3] md:aspect-auto bg-ink">
                      {r.mat.image && <img src={r.mat.image} alt={r.mat.name} className="h-full w-full object-cover" />}
                    </div>
                    <div className="md:col-span-8 p-6 md:p-8">
                      <div className="flex flex-wrap items-center gap-3">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-[11px] font-bold uppercase tracking-wide ${i === 0 ? "bg-flame text-white" : "bg-white/10 text-white/80"}`}>
                          {i === 0 ? <Trophy className="h-3.5 w-3.5" /> : <Award className="h-3.5 w-3.5" />} {r.label}
                        </span>
                        <span className="text-[11px] uppercase tracking-wide text-white/40">{r.mat.material} · {r.mat.load_rating} duty</span>
                        <span className="ml-auto text-flame font-display font-black text-2xl" data-testid={`rec-score-${r.mat.key}`}>{r.pct}%<span className="text-xs text-white/40 font-sans font-normal ml-1">match</span></span>
                      </div>
                      <h3 className="mt-3 font-display font-bold text-2xl">{r.mat.name}</h3>
                      <ul className="mt-4 space-y-2">
                        {r.reasons.map((why, k) => (
                          <li key={k} className="flex items-start gap-2.5 text-white/75 text-sm">
                            <ChevronRight className="h-4 w-4 text-flame shrink-0 mt-0.5" /> {why}
                          </li>
                        ))}
                      </ul>
                      <div className="mt-6 flex flex-wrap items-center gap-4 print:hidden">
                        <button data-testid={`rec-quote-${r.mat.key}`} onClick={() => goToQuote(r.mat.name)}
                          className="inline-flex items-center gap-2 bg-flame text-white px-6 py-3 font-semibold hover:bg-flame/90 transition-colors">
                          Get a quote for {r.mat.name} <ArrowRight className="h-4 w-4" />
                        </button>
                        <span className="text-xs text-white/40">Pricing on request</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <SpecialistFallback />
          </div>
        )}
      </div>
    </div>
  );
}

function OptionButton({ active, onClick, label, hint, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={onClick}
      className={`text-left border p-4 transition-colors ${active ? "border-flame bg-flame/10" : "border-white/12 bg-white/[0.02] hover:border-flame/50"}`}>
      <div className="flex items-center gap-2.5">
        <span className={`h-4 w-4 border flex items-center justify-center shrink-0 ${active ? "bg-flame border-flame" : "border-white/30"}`}>
          {active && <Check className="h-3 w-3 text-white" />}
        </span>
        <span className="font-semibold text-white">{label}</span>
      </div>
      {hint && <div className="mt-1 ml-7 text-xs text-white/50">{hint}</div>}
    </button>
  );
}

function FieldDark({ label, testid, type = "text", value, onChange }) {
  return (
    <div>
      <label className="block text-xs text-white/60 mb-1.5">{label}</label>
      <input data-testid={testid} type={type} value={value} onChange={onChange}
        className="w-full bg-white/5 border border-white/15 px-3.5 py-2.5 text-white placeholder:text-white/30 focus:outline-none focus:border-flame" />
    </div>
  );
}

function SpecialistFallback() {
  return (
    <div className="mt-10 pt-6 border-t border-white/10 flex flex-col sm:flex-row sm:items-center gap-3 print:hidden" data-testid="specialist-fallback">
      <span className="text-sm text-white/50">Not sure? Contact us —</span>
      <div className="flex flex-wrap gap-3">
        <a href={`tel:${COMPANY.phone}`} className="inline-flex items-center gap-2 text-flame font-semibold text-sm hover:underline">
          <Phone className="h-4 w-4" /> {COMPANY.phone}
        </a>
        <Link to="/contact" className="inline-flex items-center gap-2 text-white/70 hover:text-flame text-sm font-semibold">
          <Mail className="h-4 w-4" /> Contact form
        </Link>
      </div>
    </div>
  );
}
